import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2, Sparkles, Save, Tag, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blank = () => ({
  offer_id: null,
  title: '',
  description: '',
  discount_type: 'percent',
  discount_value: 10,
  applies_to: 'all',
  promo_code: '',
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  active: true,
});

const isCurrentlyRunning = (offer) => {
  if (!offer.active) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (offer.start_date && today < offer.start_date) return false;
  if (offer.end_date && today > offer.end_date) return false;
  return true;
};

export const CrmSpecialOffers = () => {
  const [offers, setOffers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [offRes, pkgRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/special-offers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/packages`, { withCredentials: true }),
      ]);
      setOffers(offRes.data || []);
      setPackages(pkgRes.data || []);
    } catch {
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (o) => setEditing({ ...o });
  const openCreate = () => { setCreating(true); setEditing(blank()); };
  const close = () => { setEditing(null); setCreating(false); };

  const updateField = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.title?.trim()) { toast.error('Title is required'); return; }
    if (Number(editing.discount_value) <= 0) { toast.error('Discount value must be > 0'); return; }
    if (editing.discount_type === 'percent' && Number(editing.discount_value) > 100) {
      toast.error('Percent discount cannot exceed 100');
      return;
    }
    if (editing.start_date && editing.end_date && editing.start_date > editing.end_date) {
      toast.error('Start date must be before end date');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        description: editing.description || '',
        discount_type: editing.discount_type,
        discount_value: Number(editing.discount_value),
        applies_to: editing.applies_to || 'all',
        promo_code: editing.promo_code || null,
        start_date: editing.start_date,
        end_date: editing.end_date,
        active: !!editing.active,
      };
      if (creating) {
        await axios.post(`${API_URL}/api/special-offers`.replace('/special-offers', '/admin/special-offers'), payload, { withCredentials: true });
        toast.success('Offer created');
      } else {
        await axios.patch(`${API_URL}/api/admin/special-offers/${editing.offer_id}`, payload, { withCredentials: true });
        toast.success('Offer updated');
      }
      close();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (o) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/special-offers/${o.offer_id}`,
        { active: !o.active },
        { withCredentials: true },
      );
      await load();
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`Delete offer "${o.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/special-offers/${o.offer_id}`, { withCredentials: true });
      toast.success('Offer deleted');
      await load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const grouped = useMemo(() => {
    const live = [], past = [], upcoming = [];
    const today = new Date().toISOString().slice(0, 10);
    offers.forEach((o) => {
      if (isCurrentlyRunning(o)) live.push(o);
      else if (o.end_date && o.end_date < today) past.push(o);
      else upcoming.push(o);
    });
    return { live, upcoming, past };
  }, [offers]);

  const renderCard = (o) => (
    <motion.div
      key={o.offer_id}
      layout
      className={`rounded-2xl border-2 p-4 bg-white ${
        isCurrentlyRunning(o) ? 'border-[#FF3B6B]/40' : 'border-dashed border-[#D5D2CC] opacity-90'
      }`}
      data-testid={`offer-card-${o.offer_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#1E1E24] truncate flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#FF3B6B]" />
            {o.title}
          </p>
          <p className="text-xs text-[#6B6B7A] mt-0.5">
            {o.discount_type === 'percent' ? `${o.discount_value}% off` : `$${Number(o.discount_value).toFixed(2)} off`}
            {' · '}
            {o.applies_to === 'all'
              ? 'all packages'
              : (packages.find((p) => p.id === o.applies_to)?.name || o.applies_to)}
          </p>
        </div>
        {isCurrentlyRunning(o) ? (
          <span className="px-2 py-0.5 rounded-full bg-[#06D6A0]/15 text-[10px] font-semibold text-[#06965B]">
            LIVE
          </span>
        ) : (o.end_date && o.end_date < new Date().toISOString().slice(0, 10)) ? (
          <span className="px-2 py-0.5 rounded-full bg-[#F4F1EC] text-[10px] font-semibold text-[#6B6B7A]">EXPIRED</span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-[#FFF4D6] text-[10px] font-semibold text-[#8B6914]">SCHEDULED</span>
        )}
      </div>
      {o.description && <p className="text-xs text-[#4d4d57] mt-2 line-clamp-2">{o.description}</p>}
      <div className="text-[11px] text-[#6B6B7A] mt-2 flex items-center gap-1.5">
        <Calendar className="w-3 h-3" />
        {o.start_date} → {o.end_date}
      </div>
      {o.promo_code ? (
        <div className="text-[11px] mt-1.5">
          <Tag className="inline w-3 h-3 mr-0.5" />
          Code: <span className="font-mono font-bold text-[#FF3B6B]">{o.promo_code}</span>
        </div>
      ) : (
        <div className="text-[11px] text-[#06965B] mt-1.5">Auto-applied (no code needed)</div>
      )}
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1 rounded-full" onClick={() => openEdit(o)} data-testid={`offer-edit-${o.offer_id}`}>
          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => toggleActive(o)}
          data-testid={`offer-toggle-${o.offer_id}`}
        >
          {o.active ? 'Pause' : 'Activate'}
        </Button>
        <Button variant="outline" size="sm" className="rounded-full text-red-600 hover:text-red-700" onClick={() => handleDelete(o)} data-testid={`offer-delete-${o.offer_id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6" data-testid="crm-special-offers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Special Offers</h1>
          <p className="text-sm text-[#6B6B7A] mt-1">
            Run percent or fixed-amount promotions. Auto-apply (no code) for site-wide marketing or attach a promo code for VIP campaigns.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white rounded-full"
          data-testid="offer-add-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add Offer
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#6B6B7A]">Loading…</div>
      ) : (
        <div className="space-y-6">
          {[
            { key: 'live', label: 'Live now', items: grouped.live },
            { key: 'upcoming', label: 'Upcoming', items: grouped.upcoming },
            { key: 'past', label: 'Past', items: grouped.past },
          ].map((g) => (
            <div key={g.key}>
              <h3 className="font-semibold text-sm text-[#1E1E24] mb-2">{g.label} ({g.items.length})</h3>
              {g.items.length === 0 ? (
                <p className="text-sm text-[#6B6B7A] italic mb-3">No {g.label.toLowerCase()} offers.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {g.items.map(renderCard)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-lg bg-white" data-testid="offer-edit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>
              {creating ? 'Add new offer' : `Edit "${editing?.title}"`}
            </DialogTitle>
            <DialogDescription>
              Auto-applied offers run without a promo code. Add a code to make it VIP-only.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title *</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g. Summer Special"
                  data-testid="offer-title-input"
                />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={editing.description || ''}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={2}
                  placeholder="Internal note or customer-facing tagline"
                  data-testid="offer-desc-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Discount type</Label>
                  <Select value={editing.discount_type} onValueChange={(v) => updateField('discount_type', v)}>
                    <SelectTrigger data-testid="offer-type-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="percent">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Value *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editing.discount_value}
                    onChange={(e) => updateField('discount_value', e.target.value)}
                    data-testid="offer-value-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Applies to</Label>
                <Select value={editing.applies_to || 'all'} onValueChange={(v) => updateField('applies_to', v)}>
                  <SelectTrigger data-testid="offer-applies-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All packages</SelectItem>
                    {packages.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Start date *</Label>
                  <Input
                    type="date"
                    value={editing.start_date}
                    onChange={(e) => updateField('start_date', e.target.value)}
                    data-testid="offer-start-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">End date *</Label>
                  <Input
                    type="date"
                    value={editing.end_date}
                    onChange={(e) => updateField('end_date', e.target.value)}
                    data-testid="offer-end-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Promo code (optional)</Label>
                <Input
                  value={editing.promo_code || ''}
                  onChange={(e) => updateField('promo_code', e.target.value.toUpperCase())}
                  placeholder="Leave empty for auto-apply"
                  data-testid="offer-promo-input"
                />
              </div>
              <div className="flex items-center justify-between border-t pt-3">
                <Label className="text-xs">Active</Label>
                <Switch
                  checked={!!editing.active}
                  onCheckedChange={(v) => updateField('active', v)}
                  data-testid="offer-active-toggle"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} className="rounded-full">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white rounded-full"
              data-testid="offer-save-btn"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? 'Saving…' : (creating ? 'Create offer' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmSpecialOffers;
